'use client';

/**
 * Root error boundary. Unlike src/app/error.tsx (which only catches errors in
 * the page subtree and renders inside the app layout), global-error.tsx catches
 * failures in the root layout itself, so it must render its own <html>/<body>.
 * When it is active the normal layout — including the Matomo bootstrap and
 * MatomoErrorTracker — is gone, so we report the error here directly.
 *
 * Visual style intentionally mirrors src/app/error.tsx.
 *
 * Refs: almanak-76ev.8
 */

import { useEffect } from 'react';

declare global {
  interface Window {
    _paq?: Array<Array<string | number | boolean>>;
  }
}

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    const label = error.digest ? `${error.message} [digest:${error.digest}]` : error.message;
    // _paq may not exist yet if the root layout failed before Matomo bootstrapped;
    // seed the queue so a late-loading tracker still flushes the event.
    (window._paq = window._paq || []).push(['trackEvent', 'Error', 'GlobalError', label]);
  }, [error]);

  return (
    <html>
      <body>
        <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 text-center">
          <h1 className="font-heading text-4xl font-bold text-text-primary">Something went wrong</h1>
          <p className="mt-2 text-lg text-text-secondary">An unexpected error occurred.</p>
          <button
            onClick={reset}
            className="mt-6 rounded-lg bg-brand px-5 py-2.5 text-sm font-medium text-text-on-brand transition-colors hover:bg-brand/90"
          >
            Try again
          </button>
        </div>
      </body>
    </html>
  );
}
